import {useCallback,useEffect,useRef,useState,useSyncExternalStore} from 'react'
import {readSchoolContactEnvelope,currentContactSnapshot,subscribeContactSnapshots,contactSnapshotsVersion,contactLookupLabel,contactContextChanged} from '../../../lib/currentChampionshipContacts.js'
import {schoolCoachReadBudget} from '../../../lib/schoolCoachReadBudget.js'
import ContactReadDiagnostic from './ContactReadDiagnostic.jsx'
import ContactReviewDetails,{AffiliationEvidence} from './ContactReviewDetails.jsx'

/** Read-only school fallback. Automatic only for visible, completely-read rows
 * without eligible direct contacts. No candidate becomes an outreach recipient. */
export default function SchoolCoachCandidates({recordId,viewScope,autoFallback=false,binding=''}) {
 useSyncExternalStore(subscribeContactSnapshots,contactSnapshotsVersion,contactSnapshotsVersion)
 const [chosen,setChosen]=useState(''),[visible,setVisible]=useState(false),[autoLimit,setAutoLimit]=useState(''),[retry,setRetry]=useState(0)
 const result=currentContactSnapshot(recordId,binding)?.school
 const contextChanged=contactContextChanged(recordId,binding)
 const pending=useRef(null),mounted=useRef(true),root=useRef(null),attempted=useRef(false),visibleNow=useRef(false),automaticAllowed=useRef(autoFallback)
 automaticAllowed.current=autoFallback
 useEffect(()=>{
  mounted.current=true
  const observer=typeof IntersectionObserver==='function'?new IntersectionObserver(entries=>{visibleNow.current=entries.some(entry=>entry.isIntersecting);setVisible(visibleNow.current)},{root:null,rootMargin:'0px',threshold:0}):null
  if(root.current)observer?.observe(root.current)
  return()=>{mounted.current=false;observer?.disconnect();pending.current?.controller.abort()}
 },[])
 const read=useCallback(async(automatic=false)=>{
  if(pending.current||!/^\d+$/.test(String(recordId)))return
  attempted.current=true
  const attempt={controller:new AbortController(),started:false,denied:false,completed:false}
  pending.current=attempt
  setChosen('');setAutoLimit('')
  try{
   await readSchoolContactEnvelope(recordId,{
    signal:attempt.controller.signal,binding,
    ...(automatic?{
     requireCurrentLane:true,automaticScope:viewScope,
     shouldExecute:()=>mounted.current&&visibleNow.current&&automaticAllowed.current,
     beforeDispatch:()=>{
      // Consume admission only immediately before transport, never while queued.
      const decision=schoolCoachReadBudget.claim(viewScope,recordId)
      if(decision!=='allowed'){
       attempt.denied=true
       setAutoLimit(decision==='budget_reached'?'Automatic school lookup limit reached for this view. Read this row explicitly.':'Automatic school lookup already attempted for this view. Read again if needed.')
       throw Object.assign(new Error('Automatic school read budget held'),{name:'AbortError',code:'school_read_budget'})
      }
      attempt.started=true
     },
    }:{beforeDispatch:()=>{attempt.started=true}}),
   })
   attempt.completed=true
  }catch{
   // The bound store publishes cancellation/unavailable; never revive local evidence.
  }finally{
   if(pending.current===attempt){
    pending.current=null
    // A dispatched timeout/abort consumed a real call. Only an unstarted
    // attempt may retry automatically, including StrictMode effect restarts.
    if(automatic&&!attempt.completed&&!attempt.started&&!attempt.denied){
     attempted.current=false
     if(mounted.current)setRetry(value=>value+1)
    }
   }
  }
 },[recordId,binding,viewScope])
 useEffect(()=>{
  if(!autoFallback||!visible||attempted.current||pending.current)return
  // Another owner may be cleaning up a canceled read. Loading is not a
  // completed attempt for this component; reconsider its eventual state.
  if(result?.state==='loading')return
  if(['available','partial','empty','review_required'].includes(result?.state)){attempted.current=true;return}
  void read(true)
 },[autoFallback,visible,viewScope,recordId,binding,read,retry,result?.state])
 const selected=result?.contacts?.find(contact=>contact.id===chosen)
 return <div ref={root} style={{marginTop:10}}>
  <button type="button" disabled={result?.state==='loading'} onClick={event=>{event.stopPropagation();void read()}}>{result?.state==='loading'?'Reading school coaches…':result&&result.state!=='unread'?'Read school coaches again':'Find school coach candidates'}</button>
  {contextChanged&&<small role="status" style={{display:'block'}}>The record context changed. Prior candidates were cleared; read school coaches again for fresh review-only evidence.</small>}
  {autoLimit&&<small style={{display:'block'}}>{autoLimit}</small>}
  {result&&<div role="status">
   <ContactReviewDetails value={result}/>
   {['available','partial'].includes(result.state)&&result.contacts.length?<>
    <span style={{display:'block'}}>{result.source==='exact_school_directory_potential_coaches'?'School directory candidates requiring review, not linked championship contacts.':'Potential school coaches, not linked championship contacts.'} No outreach recipient is selected.</span>
    {result.contacts.slice(0,3).map(contact=><span key={contact.id} data-school-coach-preview style={{display:'block',marginTop:8}}>
     <strong>{contact.firstName||'First name not recorded'} {contact.lastName||'Last name not recorded'}</strong>
     <span style={{display:'block'}}>{contact.email||'Email not recorded'}</span><small style={{display:'block'}}>{contact.phone||'Phone not recorded'}</small>
     <small>{contact.provenance.companyName} · {contact.provenance.role} · {contact.provenance.sport}</small>
     <AffiliationEvidence contact={contact}/>
     {(contact.reviewRequired||contact.provenance.schoolKeyConflict)&&<small style={{display:'block'}}>Identity review required before using this candidate.</small>}
    </span>)}
    {result.contacts.length>3&&<small>Showing 3 of {result.contacts.length} potential coaches. Choose another below to review.</small>}
    <select aria-label="Review a potential school coach" value={chosen} onClick={event=>event.stopPropagation()} onChange={event=>setChosen(event.target.value)} style={{width:'100%',minWidth:0}}>
     <option value="">Review a candidate, none selected</option>
     {result.contacts.map(contact=><option key={contact.id} value={contact.id}>{[contact.firstName,contact.lastName].filter(Boolean).join(' ')||'Name not recorded'} · {contact.email||'Email not recorded'}</option>)}
    </select>
    {selected&&<span style={{display:'block'}}>
     <strong>{selected.firstName||'First name not recorded'} {selected.lastName||'Last name not recorded'}</strong>
     <span style={{display:'block'}}>{selected.email||'Email not recorded'}</span><small style={{display:'block'}}>{selected.phone||'Phone not recorded'}</small>
     <small>{selected.provenance.companyName} · {selected.provenance.role} · {selected.provenance.sport}</small>
     <AffiliationEvidence contact={selected}/>
     {(selected.reviewRequired||selected.provenance.schoolKeyConflict)&&<small style={{display:'block'}}>Identity review required before using this candidate.</small>}
    </span>}
   </>:contactLookupLabel(result,{school:true})}
   <ContactReadDiagnostic value={result.diagnostics}/>
  </div>}
 </div>
}
